import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getSeats, updateSeats, getMembers, getSeatsBillingData } from 'api/account';
import dayjs from 'dayjs';
import { debounce, sortBy } from 'lodash';
import clsx from 'clsx';
import InputField from 'components/FormFields/InputField';
import { Spinner } from 'components/Loading';
import Avatar from 'components/Avatar';
import Clickwrap from 'components/Clickwrap';
import APModal from 'components/Modal/APModal';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import { imagePickSize, centsToDollars } from 'utils/general';

const SeatsModal = ({ open, onClose, orgId, planType, handleSeatsUpdate, currentPlan }) => {
  const billing = useSelector((state) => state.billing);
  const userInfo = useSelector((state) => state.global.userInfo);
  const isTrial = userInfo?.billing?.planningSubscription?.isTrial;
  const isDelinquent = userInfo?.billing?.isDelinquent;
  const { price } = currentPlan || {};

  const [seatsDefault, setSeatsDefault] = useState(0);
  const [seatsTotalOld, setSeatsTotalOld] = useState(0);
  const [seatsAdditional, setSeatsAdditional] = useState('');
  const [seatsAdditionalDiff, setSeatsAdditionalDiff] = useState(null);
  const [seatsAdditionalOld, setSeatsAdditionalOld] = useState(0);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [paymentError, setPaymentError] = useState(false);
  const [seatsAdditionalError, setSeatsAdditionalsError] = useState('');
  const [isFormValid, setFormValid] = useState(true);
  const [isAdding, setIsAdding] = useState(true);
  const [maxSeatsReached, setMaxSeatsReached] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [nextInvoiceDate, setNextInvoiceDate] = useState('');
  const [todayCharge, setTodayCharge] = useState(null);

  const nextInvoiceCharge = price + (parseInt(seatsAdditional === '' ? 0 : seatsAdditional, 10) * 1000);

  const {
      showTooltip,
      hideTooltip,
      updateTooltip,
      tooltipElement,
    } = useFloatingTooltip('To enable users, add additional seats above.');

  const seatsTotal = useMemo(() => seatsDefault + Number(seatsAdditional), [seatsDefault, seatsAdditional]);

  const updateSeatsStates = ({ total_seat_count, additional_seat_count }) => {
    setSeatsDefault(total_seat_count - additional_seat_count);
    setSeatsTotalOld(total_seat_count);
    setSeatsAdditional(additional_seat_count.toString());
    setSeatsAdditionalOld(additional_seat_count);
  };

  const handleUpdate = async () => {
    if (!billing.hasPaymentMethodType || billing.isCardExpired || isDelinquent) {
      setPaymentError(true);
      return;
    }

    const enabledMembers = members.filter(member => member.integrations_enabled).map(member => member.id);

    const data = {
      additional_seat_count: Number(seatsAdditional),
      integration_enabled_logins: enabledMembers,
    };

    const response = await updateSeats(orgId, data);

    if (response.ok) {
      // eslint-disable-next-line
      console.log('Update has been sent');
      updateSeatsStates(response.data);
      handleSeatsUpdate();
    }
  };

  const fetchSeatsBillingData = async (seatsCount) => {
    const responseBilling = await getSeatsBillingData(orgId, seatsCount);
    if (responseBilling.ok) {
      const { next_invoice_date, today_charge } = responseBilling.data;
      setNextInvoiceDate(next_invoice_date);
      // Make negative amounts positive since we change the wording from "charged" to "credited".
      setTodayCharge(Math.abs(today_charge));
      setIsLoadingPreview(false);
    }
  };

  const debouncedSeatsBilling = useMemo(() => debounce(fetchSeatsBillingData, 500), [orgId]);

  const handleAdditionalSeatsChange = useCallback((e) => {
    if (isTrial) return;
    setIsLoadingPreview(true);

    const newValue = e.target.value;

    if (newValue === '') {
      setSeatsAdditional('');
      setSeatsAdditionalsError('');
      return;
    }

    if (/^\d+$/.test(newValue)) {
      const num = Number(newValue);
      if (num < 0 || num > 100) {
        setSeatsAdditionalsError('Value must be between 0 and 100');
      } else {
        setSeatsAdditionalsError('');
      }
      setSeatsAdditional(newValue);
      debouncedSeatsBilling(num);
    }
  }, [debouncedSeatsBilling]);

  const handleAdditionalSeatsClick = () => {
    if (isTrial) {
      setError(`Your current trial limits you to ${seatsDefault} AP Connect seats.
        To purchase additional AP Connect seats, you will need to subscribe to a plan.`);
    }
  };

  const toggleSeat = (id) => {
    const newMembers = members.map(member => {
      if (member.id === id) {
        return { ...member, integrations_enabled: !member.integrations_enabled };
      }
      return member;
    });
    setMembers(newMembers);
  };

  const enabledSeatsCount = useMemo(() => members.filter(member => member.integrations_enabled).length, [members]);

  const validateSeatsLimit = (currentTotalSeats, enabledSeats) => {
    if (currentTotalSeats < enabledSeats) {
      return {
        errorMessage: `Oops! It looks like you have more seats enabled than your selected plan allows.
        You’ll need to disable at least ${enabledSeats - currentTotalSeats} seat(s) to downgrade.`,
        isValid: false,
      };
    }
    return { errorMessage: '', isValid: true };
  };

  useEffect(() => {
    const checkSeatsLimit = () => {
      const { errorMessage, isValid } = validateSeatsLimit(
        seatsTotal,
        enabledSeatsCount,
      );
      setError(errorMessage);
      setFormValid(isValid);
    };
    checkSeatsLimit();
    setMaxSeatsReached(enabledSeatsCount >= seatsTotal);
  }, [enabledSeatsCount, seatsTotal]);

  useEffect(() => {
    if (Number(seatsAdditional) > seatsAdditionalOld) {
      setIsAdding(true);
    } else {
      setIsAdding(false);
    }

    setSeatsAdditionalDiff(Math.abs(Number(seatsAdditional) - seatsAdditionalOld));
  }, [seatsAdditional]);

  useEffect(() => {
    if (!open) return;

    setError('');
    setSeatsAdditionalsError('');
    setFormValid(true);
    setMaxSeatsReached(false);
    setSeatsAdditionalDiff(null);

    const fetchData = async () => {
      const [responseSeats, responseMembers] = await Promise.all([
        getSeats(orgId),
        getMembers(orgId),
      ]);

      // #TODO: Add error state for when either response fails. Need all the data.
      if (responseSeats.ok && responseMembers.ok) {
        updateSeatsStates(responseSeats.data);
        const sortedMembers = sortBy(
          responseMembers.data.filter(member => !member.is_placeholder),
          ['firstname'],
        );
        setMembers(sortedMembers);
        setIsLoading(false);
      }
    };

    fetchData();

  }, [open]);

  return (
    <APModal
      open={open}
      onClose={() => onClose(false)}
      title='Manage AP Connect Seats'
      name="manage-seats"
      isLoading={isLoading}
      errorMsg={error}
      actions={(
        <>
          <button
            type="button"
            className="button button-cancel"
            onClick={() => onClose(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button button-delete"
            onClick={handleUpdate}
            disabled={!isFormValid || seatsAdditionalError.length > 0 || paymentError}
          >
            Update seats
          </button>
        </>
      )}
    >
      <>
        <p>
          You currently have a total of {seatsTotalOld} AP Connect seats. You can add or adjust
          additional seats at any time and manage team member seats below.
        </p>

        <div className="row">
          <div className="row__label">INCLUDED (FREE):</div>
          <div className="row__value">{seatsDefault}</div>
        </div>
        <div className="row additional-seats">
          <div className="row__label">ADDITIONAL SEATS ($10 PER SEAT/MONTH):</div>
          <div className="row__value">
            <InputField
              name="seatsAdditional"
              showLabel={false}
              value={seatsAdditional}
              onChange={handleAdditionalSeatsChange}
              onClick={handleAdditionalSeatsClick}
              onBlur={(e) => {
                if (e.target.value === '') {
                  setSeatsAdditional('0');
                }
              }}
              type="number"
              min="0"
              max="100"
              error={seatsAdditionalError}
              showError={seatsAdditionalError.length > 0}
            />
          </div>
        </div>
        <div className="row">
          <div className="row__label">Total Seats:</div>
          <div className="row__value">{seatsTotal}</div>
        </div>

        {(Number(seatsAdditional) !== seatsAdditionalOld) && !seatsAdditionalError.length > 0 ? (
          <>
            {isLoadingPreview ? (
              <div className="manage-ap-connect__loader">
                <Spinner />
              </div>
            ) : (
              <div className="text-block">
                <p>
                  You'll be {isAdding ? 'charged' : 'credited'} {centsToDollars(todayCharge)} for
                  {isAdding ? ' adding' : ' removing'} {seatsAdditionalDiff} seat(s) today,
                  prorated for the current billing period.
                  Starting {dayjs(nextInvoiceDate).format('MMMM Do, YYYY')}, your
                  new {planType === 'month' ? 'monthly' : 'annual'} total
                  will be {centsToDollars(nextInvoiceCharge)} before any discounts, credits, and taxes.
                </p>

                <p>
                  We’ll charge your payment method on file. Updates to your payment method can be made from the{' '}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                  >
                    Subscription & Billing page
                  </a>.
                </p>
              </div>
            )}
          </>
        ) : null}

        <h6>
          MANAGE TEAM MEMBER SEATS:
        </h6>
        {members.map(member => {
          const isDisabled = maxSeatsReached && !member.integrations_enabled;
          const profileImage = member.profile_image ?? null;
          let imagePath = null;
          if (profileImage) {
            imagePath = `//${process.env.REACT_APP_AWS_CLOUDFRONT}/${imagePickSize(
              profileImage,
              'square60',
            )}`;
          }

          return (
            <div
              className="member-seat"
              key={`member-${member.id}`}
            >
              <div className="member-seat__image">
                <Avatar
                  photo={imagePath}
                  text={!imagePath ? member.initials : null}
                  size={30}
                  borderSize={1}
                />
              </div>
              <div className="member-seat__name">
                {member.firstname} {member.lastname}
              </div>
              <div
                className="member-seat__toggle"
                onMouseEnter={e => {
                  if (isDisabled) {
                    showTooltip();
                    updateTooltip(e);
                  }
                }}
                onMouseMove={e => {
                  if (isDisabled) {
                    updateTooltip(e);
                  }
                }}
                onMouseLeave={hideTooltip}
              >
                <Clickwrap
                  name={`member-${member.id}`}
                  checked={member.integrations_enabled}
                  className={clsx('clickwrap--view-option small', isDisabled && 'disabled')}
                  onChange={() => !isDisabled && toggleSeat(member.id)}
                />
                <label htmlFor={`member-${member.id}`}>
                  {member.integrations_enabled ? 'Enabled' : 'Enable'}
                </label>
                {isDisabled && tooltipElement}
              </div>
            </div>
          );
        })}

        {paymentError ? (
          <p className="payment-error">
            There was an issue with processing your payment. Please verify
            your card details on the Subscriptions & Billing page. If the problem persists,
            contact your bank or try using a different payment method.
          </p>
        ) : null}
      </>
    </APModal>
  );
};

export default SeatsModal;
